import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  Sparkles,
  LayoutDashboard,
  Inbox as InboxIcon,
  Users,
  Mail,
  Headphones,
  DollarSign,
  Handshake,
  TrendingUp,
  MessageSquare,
  Phone,
  Network,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  LogOut,
  X,
} from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';

const navItems = [
  { to: '/today', icon: Sparkles, label: 'Today' },
  { to: '/inbox', icon: InboxIcon, label: 'Inbox' },
  { to: '/', icon: LayoutDashboard, label: 'Command Center' },
  { to: '/contacts', icon: Users, label: 'Contacts' },
  { to: '/email', icon: Mail, label: 'Email Ops' },
  { to: '/support', icon: Headphones, label: 'Support' },
  { to: '/finance', icon: DollarSign, label: 'Finance' },
  { to: '/partnerships', icon: Handshake, label: 'Partnerships' },
  { to: '/growth', icon: TrendingUp, label: 'Growth Engine' },
  { to: '/messaging', icon: MessageSquare, label: 'Messaging' },
  { to: '/voice', icon: Phone, label: 'Voice Ops' },
  { to: '/context', icon: Network, label: 'Context Graph' },
  { to: '/settings', icon: Settings, label: 'Settings' },
  { to: '/audit', icon: Shield, label: 'Audit Log' },
];

interface SidebarProps {
  /** Whether the mobile drawer is open (only relevant on <md screens) */
  mobileOpen?: boolean;
  /** Callback to close the mobile drawer */
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, tenant, logout } = useAuthStore();
  const location = useLocation();

  // Auto-close mobile drawer on route change
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sidebarContent = (
    <>
      {/* Logo */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-surface-700 flex-shrink-0">
        <div className="flex items-center justify-center w-8 h-8 bg-nexus-600 rounded-md flex-shrink-0">
          <span className="text-sm font-black text-white leading-none">N</span>
        </div>
        {!collapsed && (
          <div className="min-w-0 flex-1">
            <h1 className="text-base font-black text-surface-100 tracking-tight">Nexus</h1>
          </div>
        )}
        {/* Mobile close button — only visible in mobile drawer */}
        {onMobileClose && (
          <button
            onClick={onMobileClose}
            className="md:hidden p-1.5 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-800 transition-colors ml-auto"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        <ul className="space-y-0.5">
          {navItems.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-nexus-600/15 text-nexus-400 border border-nexus-500/20'
                      : 'text-surface-400 hover:text-surface-200 hover:bg-surface-800 border border-transparent',
                    collapsed && 'justify-center px-2',
                  )
                }
                title={collapsed ? item.label : undefined}
              >
                <item.icon className="h-[18px] w-[18px] flex-shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      {/* Collapse toggle — hidden on mobile drawer (always expanded) */}
      <div className="hidden md:block px-2 py-2 border-t border-surface-700">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-surface-400 hover:text-surface-200 hover:bg-surface-800 transition-colors cursor-pointer"
        >
          {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          {!collapsed && <span className="text-xs">Collapse</span>}
        </button>
      </div>

      {/* User section */}
      <div className="px-3 py-3 border-t border-surface-700 flex-shrink-0">
        <div className={clsx('flex items-center', collapsed ? 'justify-center' : 'gap-3')}>
          <div className="w-8 h-8 rounded-full bg-nexus-700 flex items-center justify-center flex-shrink-0 text-xs font-bold text-nexus-200">
            {user?.name?.charAt(0).toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-surface-200 truncate">{user?.name || 'User'}</p>
              <p className="text-xs text-surface-500 truncate">{tenant?.name || 'Tenant'}</p>
            </div>
          )}
          {!collapsed && (
            <button
              onClick={() => logout()}
              className="p-1.5 rounded-lg text-surface-500 hover:text-red-400 hover:bg-surface-800 transition-colors cursor-pointer"
              title="Logout"
            >
              <LogOut className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      {/* Desktop sidebar — hidden on mobile */}
      <aside
        className={clsx(
          'hidden md:flex flex-col h-screen bg-surface-900 border-r border-surface-700 transition-all duration-200',
          collapsed ? 'w-16' : 'w-60',
        )}
      >
        {sidebarContent}
      </aside>

      {/* Mobile drawer overlay — only on <md screens */}
      {mobileOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
            onClick={onMobileClose}
          />
          {/* Drawer */}
          <aside className="md:hidden fixed inset-y-0 left-0 z-50 flex flex-col w-64 bg-surface-900 border-r border-surface-700 shadow-2xl shadow-black/50 animate-slide-in-left">
            {sidebarContent}
          </aside>
        </>
      )}
    </>
  );
}
